/**
 * Milestone 26 — First SEO content set (Medellín / Antioquia).
 * Unique copy per page; avoid duplicating paragraphs across configs.
 */

export const M26_ANTIOQUIA_CITY_SLUGS = [
  "medellin",
  "envigado",
  "sabaneta",
  "itagui",
  "bello",
  "rionegro",
];

export const M26_MEDELLIN_BRAND_SLUGS = [
  "mazda",
  "toyota",
  "renault",
  "chevrolet",
  "kia",
  "hyundai",
  "nissan",
  "ford",
];

export const M26_MEDELLIN_MODELS = [
  { makeSlug: "mazda", makeName: "Mazda", modelSlug: "cx-5", modelName: "CX-5" },
  { makeSlug: "toyota", makeName: "Toyota", modelSlug: "prado", modelName: "Prado" },
  { makeSlug: "renault", makeName: "Renault", modelSlug: "duster", modelName: "Duster" },
  { makeSlug: "chevrolet", makeName: "Chevrolet", modelSlug: "onix", modelName: "Onix" },
  { makeSlug: "kia", makeName: "Kia", modelSlug: "picanto", modelName: "Picanto" },
  { makeSlug: "hyundai", makeName: "Hyundai", modelSlug: "tucson", modelName: "Tucson" },
  { makeSlug: "nissan", makeName: "Nissan", modelSlug: "frontier", modelName: "Frontier" },
  { makeSlug: "ford", makeName: "Ford", modelSlug: "escape", modelName: "Escape" },
];

export const M26_MEDELLIN_BUDGET_SLUGS = [
  "hasta-30-millones",
  "30-a-50-millones",
  "50-a-80-millones",
  "80-a-120-millones",
];

export const CITY_INTRO_COPY = {
  medellin:
    "Explora carros usados disponibles en Medellín con información clara de precio, kilometraje, concesionario, disponibilidad y señales de confianza de AutoMapa.",
  envigado:
    "En Envigado puedes comparar carros usados con inventario del sur del Valle de Aburrá, filtrar por presupuesto y contactar concesionarios con señales de verificación antes de agendar una visita.",
  sabaneta:
    "Sabaneta concentra buena oferta de carros usados para quienes buscan opciones compactas y familiares cerca del sur del área metropolitana. Revisa precio, año y checklist antes de contactar.",
  itagui:
    "Itagüí es un punto práctico para buscar carros usados con acceso rápido al sur del Valle de Aburrá. Compara publicaciones activas y valida documentos antes de pagar.",
  bello:
    "Bello amplía la búsqueda de carros usados hacia el norte del área metropolitana. Filtra por tipo de carrocería, transmisión y concesionarios verificados en AutoMapa.",
  rionegro:
    "Rionegro y oriente antioqueño ofrecen alternativas para quien busca carros usados fuera del centro de Medellín. Explora inventario disponible y contacta con contexto desde cada publicación.",
};

export const CITY_META_DESCRIPTIONS = {
  medellin:
    "Encuentra carros usados en Medellín con precios comparables, concesionarios verificados, checklist de compra y contacto por WhatsApp en AutoMapa.",
  envigado:
    "Compara carros usados en Envigado. Revisa kilometraje, señales de precio justo y concesionarios verificados antes de contactar en AutoMapa.",
  sabaneta:
    "Busca carros usados en Sabaneta con inventario activo, señales de confianza y guías prácticas de compra en AutoMapa.",
  itagui:
    "Explora carros usados en Itagüí con filtros por presupuesto, marca y tipo de vehículo. Contacto por WhatsApp en AutoMapa.",
  bello:
    "Encuentra carros usados en Bello con concesionarios verificados, indicadores de riesgo y checklist de compra en AutoMapa.",
  rionegro:
    "Compara carros usados en Rionegro y oriente antioqueño. Precio, disponibilidad y contacto con concesionarios en AutoMapa.",
};

export const BODY_TYPE_MEDELLIN_COPY = {
  camionetas: {
    intro:
      "Las camionetas usadas son una buena opción para familias, viajes, lomas y recorridos dentro del Valle de Aburrá. Compara SUV publicadas en Medellín con señales de precio y verificación de concesionario.",
    meta: "Camionetas usadas en Medellín con precios comparables, concesionarios verificados y contacto por WhatsApp en AutoMapa.",
  },
  automoviles: {
    intro:
      "Los automóviles usados en Medellín son ideales para ciudad, trabajo diario y desplazamientos en el área metropolitana. Filtra por sedán, año y presupuesto antes de contactar.",
    meta: "Automóviles usados en Medellín. Compara precio, kilometraje y concesionarios verificados en AutoMapa.",
  },
  pickups: {
    intro:
      "Las pickups usadas en Medellín sirven para trabajo, carga liviana y recorridos mixtos ciudad-carretera. Revisa historial, kilometraje y condiciones antes de cerrar trato.",
    meta: "Pickups usadas en Medellín con inventario activo y señales de confianza en AutoMapa.",
  },
  familiares: {
    intro:
      "Los carros familiares usados en Medellín suelen ofrecer buen espacio interior para familias y trayectos urbanos. Compara hatchbacks y compactos con checklist de compra.",
    meta: "Carros familiares usados en Medellín. Encuentra opciones prácticas con AutoMapa.",
  },
};

export const BRAND_MEDELLIN_INTRO = {
  mazda: "Mazda usados en Medellín: compara CX-5 y otros modelos con inventario de concesionarios verificados. Revisa precio frente a similares antes de contactar.",
  toyota: "Toyota usados en Medellín incluyen opciones populares para ciudad y carretera. Explora Prado y otros modelos con señales de confianza en AutoMapa.",
  renault: "Renault usados en Medellín, incluido Duster y compactos, con filtros por presupuesto y contacto directo por WhatsApp desde cada publicación.",
  chevrolet: "Chevrolet usados en Medellín: desde compactos hasta camionetas. Compara Onix y otras versiones con indicadores de precio cuando hay datos.",
  kia: "Kia usados en Medellín con foco en compactos y urbanos como Picanto. Verifica documentos y kilometraje con nuestro checklist antes de pagar.",
  hyundai: "Hyundai usados en Medellín, incluido Tucson y sedanes, con concesionarios verificados y señales de riesgo cuando aplican.",
  nissan: "Nissan usados en Medellín: explora Frontier, sedanes y camionetas publicadas con información clara de precio y disponibilidad.",
  ford: "Ford usados en Medellín, incluido Escape y otros modelos, con inventario activo y guías de compra segura en AutoMapa.",
};

export const MODEL_MEDELLIN_INTRO = {
  "mazda/cx-5":
    "Mazda CX-5 usados en Medellín: SUV popular por confort y uso mixto ciudad-vía. Compara versiones, años y kilometraje antes de contactar al concesionario.",
  "toyota/prado":
    "Toyota Prado usados en Medellín: revisa historial, precio frente a similares y estado documental. Ideal para familias y viajes; verifica con checklist completo.",
  "renault/duster":
    "Renault Duster usados en Medellín: opción versátil para ciudad y caminos. Compara versiones 4x2/4x4, kilometraje y señales de precio en AutoMapa.",
  "chevrolet/onix":
    "Chevrolet Onix usados en Medellín: compacto frecuente para primer carro o uso urbano. Revisa SOAT, tecnomecánica y coherencia de precio antes de pagar.",
  "kia/picanto":
    "Kia Picanto usados en Medellín: económico y práctico para trayectos cortos. Explora opciones disponibles y contacta con contexto por WhatsApp.",
  "hyundai/tucson":
    "Hyundai Tucson usados en Medellín: camioneta urbana con buen espacio. Compara versiones, año y precio con publicaciones similares en la zona.",
  "nissan/frontier":
    "Nissan Frontier usadas en Medellín: pickup para trabajo y carga. Inspecciona chasis, kilometraje y documentos antes de transferir.",
  "ford/escape":
    "Ford Escape usados en Medellín: SUV compacta para familia y ciudad. Revisa mantenimientos, precio comparable y verificación del concesionario.",
};

export const BUDGET_MEDELLIN_GUIDANCE = {
  "hasta-30-millones":
    "En este rango suele haber compactos y sedanes con mayor kilometraje. Prioriza historial documental, estado mecánico y coherencia del precio frente a similares.",
  "30-a-50-millones":
    "Rango popular para primer vehículo o cambio urbano. Compara año, kilometraje y costos de traspaso dentro de tu presupuesto total.",
  "50-a-80-millones":
    "Aquí aparecen camionetas, sedanes recientes y opciones familiares. Usa señales de precio justo y checklist antes de contactar.",
  "80-a-120-millones":
    "Presupuesto para modelos más recientes o mejor equipados. Verifica versión, historial y condiciones mecánicas con calma antes de pagar.",
};

export const FINANCING_DISCLAIMER =
  "Las condiciones de financiación dependen del concesionario o entidad financiera. Revisa tasa, cuota inicial, plazo y costos adicionales. AutoMapa no otorga crédito.";

export function commonFaqs(cityName = "Medellín") {
  return [
    {
      question: "¿Cómo saber si un carro usado tiene precio justo?",
      answer:
        "AutoMapa compara el precio con vehículos similares en la zona cuando hay datos. También puedes revisar año, kilometraje, versión y estado general antes de contactar.",
    },
    {
      question: "¿Qué debo revisar antes de contactar un concesionario?",
      answer:
        "Confirma disponibilidad, pide SOAT y tecnomecánica vigentes, revisa el checklist de compra y verifica identidad del vendedor antes de pagar anticipos.",
    },
    {
      question: "¿Qué significa concesionario verificado en AutoMapa?",
      answer:
        "Significa que revisamos datos básicos del negocio y su canal de contacto. No garantiza el estado mecánico, legal ni documental de cada vehículo.",
    },
    {
      question: "¿Puedo contactar por WhatsApp?",
      answer: `Sí. Desde cada publicación puedes escribir al concesionario conservando datos del vehículo, útil para compradores en ${cityName} y alrededores.`,
    },
    {
      question: "¿Qué documentos debo revisar antes de comprar?",
      answer:
        "SOAT, tecnomecánica, tarjeta de propiedad, coherencia placa-VIN, multas y proceso de traspaso. Consulta nuestra guía de SOAT y tecnomecánica para más detalle.",
    },
  ];
}

export const M26_GUIDES = [
  {
    slug: "que-revisar-antes-de-comprar-carro-usado",
    title: "Qué revisar antes de comprar un carro usado",
    metaTitle: "Qué revisar antes de comprar un carro usado | Guía AutoMapa",
    metaDescription:
      "Documentos, SOAT, tecnomecánica, inspección mecánica, precio, verificación de concesionario y seguridad de pago antes de comprar un carro usado.",
    introCopy:
      "Esta guía resume lo esencial que conviene revisar antes de pagar o firmar por un carro usado en Colombia. AutoMapa organiza señales de confianza, pero la verificación final es responsabilidad del comprador.",
    sections: [
      { id: "documentos", title: "Documentos básicos", body: "Tarjeta de propiedad, identificación del vendedor, coherencia entre placa, VIN y titular registrado." },
      { id: "soat", title: "SOAT vigente", body: "Confirma vigencia y que corresponda al vehículo. Verifica en fuentes oficiales antes de asumir lo que dice el vendedor." },
      { id: "tecno", title: "Tecnomecánica", body: "Revisa fecha de vencimiento y resultado. Un vehículo sin revisión al día puede implicar costos inmediatos." },
      { id: "mecanica", title: "Inspección mecánica", body: "Ideal con taller de confianza: frenos, suspensión, fugas, scanner y prueba en ruta." },
      { id: "precio", title: "Comparación de precio", body: "Compara con publicaciones similares por año, kilometraje y versión. Usa la señal de precio justo como referencia, no como tasación formal." },
      { id: "concesionario", title: "Verificación del concesionario", body: "El badge verificado indica revisión básica del negocio y contacto. No sustituye verificar cada vehículo." },
      { id: "pago", title: "Seguridad de pago", body: "Evita anticipos sin ver el carro. Prefiere pagos trazables y conserva comprobantes." },
      { id: "traspaso", title: "Proceso de traspaso", body: "Acuerda quién gestiona el trámite, tiempos y costos antes de entregar dinero." },
    ],
    relatedGuides: [
      { label: "Checklist de compra", href: "/guias/checklist-compra-carro-usado" },
      { label: "SOAT y tecnomecánica", href: "/guias/soat-y-tecnomecanica-carro-usado" },
      { label: "Evitar estafas", href: "/guias/como-evitar-estafas-comprando-carro-usado" },
    ],
    disclaimer: "AutoMapa no presta asesoría legal ni garantiza el estado de vehículos publicados.",
    priority: 0.7,
  },
  {
    slug: "como-saber-si-un-precio-es-justo",
    title: "Cómo saber si un precio es justo",
    metaTitle: "Cómo saber si un precio es justo en carros usados | AutoMapa",
    metaDescription:
      "Compara publicaciones similares, entiende la señal de precio justo y cuándo desconfiar de precios muy bajos o muy altos.",
    introCopy:
      "Un precio justo depende de año, kilometraje, versión, ciudad, condición y demanda local. AutoMapa contextualiza precios con vehículos similares cuando hay datos suficientes.",
    sections: [
      { id: "similares", title: "Compara con similares", body: "Mira mismas marca, modelo cercano, año parecido y kilometraje comparable en Medellín o tu ciudad." },
      { id: "factores", title: "Año, km y versión", body: "Dos vehículos con el mismo nombre pueden valer distinto por equipamiento, historial y estado." },
      { id: "badge", title: "Señal de precio justo", body: "Cuando hay datos, AutoMapa muestra si el precio está cerca del rango observado en publicaciones comparables." },
      { id: "bajo", title: "Precio muy bajo", body: "Desconfía si el precio está muy por debajo sin explicación clara. Puede ocultar problemas mecánicos o documentales." },
      { id: "alto", title: "Precio alto", body: "Un precio alto puede reflejar bajo kilometraje o mejor versión, pero conviene comparar alternativas antes de contactar." },
      { id: "disclaimer", title: "No es una tasación", body: "La señal de AutoMapa no reemplaza peritaje, tasación formal ni garantía de reventa." },
    ],
    relatedGuides: [
      { label: "Qué revisar antes de comprar", href: "/guias/que-revisar-antes-de-comprar-carro-usado" },
      { label: "Checklist", href: "/guias/checklist-compra-carro-usado" },
    ],
    disclaimer: "Las señales de precio son orientativas y pueden variar según inventario disponible.",
    priority: 0.7,
  },
  {
    slug: "como-comprar-carro-usado-en-medellin",
    title: "Cómo comprar un carro usado en Medellín",
    metaTitle: "Cómo comprar un carro usado en Medellín | Guía AutoMapa",
    metaDescription:
      "Pasos para buscar, inspeccionar, probar y cerrar la compra de un carro usado en Medellín con más confianza.",
    introCopy:
      "Comprar en Medellín implica comparar opciones en el Valle de Aburrá, validar documentos y coordinar visitas con concesionarios o vendedores. Esta guía ordena el proceso paso a paso.",
    sections: [
      { id: "busqueda", title: "Busca con filtros claros", body: "Define presupuesto, ciudad, tipo de carrocería y transmisión. Usa AutoMapa para acotar opciones reales." },
      { id: "contacto", title: "Contacto con concesionario", body: "Escribe por WhatsApp desde la publicación para conservar datos del vehículo y hacer preguntas concretas." },
      { id: "inspeccion", title: "Inspección y prueba", body: "Visita en horario diurno, revisa exterior/interior y agenda prueba de manejo en lugar seguro." },
      { id: "documentos", title: "Documentos locales", body: "Verifica SOAT, tecnomecánica, multas y traspaso en RUNT u oficinas autorizadas según corresponda." },
      { id: "pago", title: "Pago seguro", body: "No envíes anticipos sin ver el carro. Acuerda forma de pago trazable y conserva recibos." },
      { id: "traspaso", title: "Traspaso", body: "Confirma responsables, tiempos y costos del trámite antes de cerrar." },
    ],
    relatedGuides: [
      { label: "Carros usados Medellín", href: "/carros-usados-medellin" },
      { label: "Checklist", href: "/guias/checklist-compra-carro-usado" },
    ],
    inventoryFilters: { city: "Medellín" },
    priority: 0.75,
  },
  {
    slug: "soat-y-tecnomecanica-carro-usado",
    title: "SOAT y tecnomecánica en carros usados",
    metaTitle: "SOAT y tecnomecánica en carros usados | Guía AutoMapa",
    metaDescription:
      "Qué pedir, por qué importa la vigencia y cómo verificar SOAT y tecnomecánica antes de comprar un carro usado.",
    introCopy:
      "SOAT y tecnomecánica vigentes son señales básicas de cumplimiento, pero siempre debes verificar el estado oficial del vehículo por tu cuenta.",
    sections: [
      { id: "pedir", title: "Qué pedir al vendedor", body: "Copia o foto legible de SOAT y certificado de revisión técnico-mecánica con fechas visibles." },
      { id: "vigencia", title: "Por qué importa la vigencia", body: "Documentos vencidos implican gastos inmediatos y pueden retrasar traspaso o circulación legal." },
      { id: "verificar", title: "Verificación oficial", body: "Consulta en fuentes oficiales o entidades autorizadas. No te quedes solo con lo que muestra el anuncio." },
      { id: "limites", title: "Límites de esta guía", body: "AutoMapa no presta asesoría legal. Ante dudas complejas, consulta un profesional o la entidad competente." },
    ],
    relatedGuides: [
      { label: "Qué revisar antes de comprar", href: "/guias/que-revisar-antes-de-comprar-carro-usado" },
    ],
    disclaimer: "Esta guía es informativa y no constituye asesoría legal.",
    priority: 0.7,
  },
  {
    slug: "como-evitar-estafas-comprando-carro-usado",
    title: "Cómo evitar estafas comprando carro usado",
    metaTitle: "Cómo evitar estafas al comprar carro usado | AutoMapa",
    metaDescription:
      "Señales de alerta, pagos seguros, verificación de identidad y cuándo reportar publicaciones sospechosas.",
    introCopy:
      "Las estafas en compraventa de vehículos usados suelen apoyarse en prisa, precios irreales y pagos adelantados. Estas recomendaciones reducen riesgo, sin eliminarlo por completo.",
    sections: [
      { id: "anticipos", title: "Evita anticipos", body: "No transfieras dinero sin ver el vehículo y validar documentos en persona o con intermediario confiable." },
      { id: "identidad", title: "Verifica identidad", body: "Confirma que quien vende puede transferir el vehículo y que los datos coinciden con la documentación." },
      { id: "lugar", title: "Lugar y horario seguros", body: "Prefiere encuentros diurnos en sitios públicos o concesionarios con presencia verificable." },
      { id: "precio", title: "Precios demasiado bajos", body: "Ofertas muy por debajo del mercado suelen ser señal de fraude o problemas ocultos." },
      { id: "traspaso", title: "Traspaso oficial", body: "Cierra con procesos documentados. Desconfía de ventas solo por chat sin papeles claros." },
      { id: "reportar", title: "Reportar sospechas", body: "Usa la opción de reportar en publicaciones o perfiles de AutoMapa si algo no cuadra." },
    ],
    relatedGuides: [
      { label: "Compra segura", href: "/guias/compra-segura" },
      { label: "Checklist", href: "/guias/checklist-compra-carro-usado" },
    ],
    priority: 0.72,
  },
];
